<template>
  <div :class="darkModeClass" class="tabs-root" :data-accent="panel.currentColor || 'primary'">
    <div class="tab-group">
      <slot>
        <Heading v-if="panel.showTitle" :level="1" v-text="panel.name" />

        <p
          v-if="panel.helpText"
          :class="panel.helpText ? 'tabs-mt-2' : 'tabs-mt-3'"
          class="tabs-text-gray-500 tabs-text-sm tabs-font-semibold tabs-italic"
          v-html="panel.helpText"
        ></p>
      </slot>

      <div
        class="tabs-shell"
        :class="[panel.showTitle && !panel.showToolbar ? 'tabs-mt-3' : '']"
      >
        <div id="tabs" class="tabs-nav-wrap">
          <div class="tabs-nav-scroll">
            <nav aria-label="Tabs" class="tabs-nav nova-tabs">
              <a
                v-for="(tab, key) in getSortedTabs(tabs)"
                :key="key"
                :class="{ 'is-active': getIsTabCurrent(tab) }"
                :dusk="tab.slug + '-tab'"
                :ref="tab.slug + '-tab'"
                class="tab-item"
                :style="getTabWidth()"
                @click.prevent="handleTabClick(tab)"
              >
                <span class="tab-item__label">{{ tab.properties.title }}</span>
                <span aria-hidden="true" class="tab-indicator"></span>
              </a>
            </nav>
          </div>
        </div>

        <div
          v-for="(tab, index) in getSortedTabs(tabs)"
          v-show="getIsTabCurrent(tab)"
          :key="'related-tabs-fields' + index"
          :ref="getTabRefName(tab)"
          :class="['tab', getIsTabCurrent(tab) ? 'block' : 'hidden', tab.slug]"
          :label="tab.name"
        >
          <div class="tabs-content">
            <KeepAlive>
              <template
                v-for="(field, index) in tab.fields"
                :key="'tab-' + index"
              >
                <component
                  v-if="!field.from"
                  :is="getComponentName(field)"
                  ref="fields"
                  :class="{
                    'remove-bottom-border': index === tab.fields.length - 1,
                  }"
                  :errors="validationErrors"
                  :field="field"
                  :form-unique-id="formUniqueId"
                  :related-resource-id="relatedResourceId"
                  :related-resource-name="relatedResourceName"
                  :resource-id="resourceId"
                  :resource-name="resourceName"
                  :show-help-text="field.helpText != null"
                  :shown-via-new-relation-modal="shownViaNewRelationModal"
                  :via-relationship="viaRelationship"
                  :via-resource="viaResource"
                  :via-resource-id="viaResourceId"
                  @field-changed="$emit('field-changed')"
                  @file-deleted="$emit('update-last-retrieved-at-timestamp')"
                  @file-upload-started="$emit('file-upload-started')"
                  @file-upload-finished="$emit('file-upload-finished')"
                />

                <component
                  v-if="field.from"
                  :is="getComponentName(field)"
                  :errors="validationErrors"
                  :resource-id="getResourceId(field)"
                  :resource-name="field.resourceName || resourceName"
                  :field="field"
                  :via-resource="field.from.viaResource"
                  :via-resource-id="field.from.viaResourceId"
                  :via-relationship="field.from.viaRelationship"
                  :form-unique-id="relationFormUniqueId"
                  @field-changed="$emit('field-changed')"
                  @file-deleted="$emit('update-last-retrieved-at-timestamp')"
                  @file-upload-started="$emit('file-upload-started')"
                  @file-upload-finished="$emit('file-upload-finished')"
                  :show-help-text="field.helpText != null"
                />
              </template>
            </KeepAlive>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BehavesAsPanel from '../mixins/BehavesAsPanel'
import HasTabs from '../mixins/HasTabs'

export default {
  mixins: [BehavesAsPanel, HasTabs],
  data: () => ({
    tabMode: 'form',
  }),
  methods: {
    getAllFieldsExceptRelatable(tab) {
      return tab.fields.filter(field => !field.relatable)
    },
    getAllRelatableFields(tab) {
      return tab.fields.filter(field => field.relatable)
    },
    getTabWidth() {
      const tabsCount = this.getSortedTabs(this.tabs).length
      const minTabsForScroll = 5

      if (tabsCount < minTabsForScroll) {
        const widthPercent = 100 / tabsCount

        return {
          '--tab-width-desktop': `${widthPercent}%`,
          '--tab-width-mobile': `max(9.5rem, ${Math.min(widthPercent, 50)}%)`,
        }
      }

      return {
        '--tab-width-desktop': '20%',
        '--tab-width-mobile': '10.5rem',
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.tabs-shell {
  overflow: hidden;
  border: 1px solid rgba(var(--colors-gray-200));
  border-radius: 1rem;
  background-color: rgb(var(--colors-white));
}

.dark .tabs-shell {
  border-color: rgba(var(--colors-gray-700));
  background-color: rgb(var(--colors-gray-800));
}

.tabs-nav-wrap {
  border-bottom: 1px solid rgba(var(--colors-gray-200));
  background-color: rgb(var(--colors-gray-50));
}

.dark .tabs-nav-wrap {
  border-bottom-color: rgba(var(--colors-gray-700));
  background-color: rgb(var(--colors-gray-900));
}

.tabs-nav-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}

.tabs-nav {
  display: flex;
  min-width: min-content;
}

.tab-item {
  position: relative;
  display: block;
  flex: 0 0 auto;
  min-width: var(--tab-width-mobile, 45%);
  min-height: 3.25rem;
  padding: 0.75rem 0.875rem;
  cursor: pointer;
  text-align: center;
  font-size: 0.8125rem;
  line-height: 1.35;
  font-weight: 500;
  color: rgba(var(--colors-gray-500));
  background-color: transparent;
  border-inline-end: 1px solid rgba(var(--colors-gray-200));
  transition: color 0.15s ease, background-color 0.15s ease;
}

.dark .tab-item {
  color: rgba(var(--colors-gray-400));
  border-inline-end-color: rgba(var(--colors-gray-700));
}

.tab-item:last-child {
  border-inline-end: none;
}

.tab-item:hover {
  color: rgba(var(--colors-gray-700));
  background-color: rgba(var(--colors-gray-100), 0.65);
}

.dark .tab-item:hover {
  color: rgba(var(--colors-gray-200));
  background-color: rgba(var(--colors-gray-800), 0.85);
}

.tab-item__label {
  display: block;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.tab-indicator {
  position: absolute;
  inset-inline: 0;
  bottom: 0;
  height: 2px;
  background-color: transparent;
}

.tabs-root[data-accent='primary'] {
  --tab-accent: var(--colors-primary-500);
}

.tabs-root[data-accent='sky'] {
  --tab-accent: var(--colors-sky-500);
}

.tabs-root[data-accent='blue'] {
  --tab-accent: var(--colors-blue-500);
}

.tabs-root[data-accent='indigo'] {
  --tab-accent: var(--colors-indigo-500);
}

.tab-item.is-active {
  color: rgba(var(--tab-accent, var(--colors-primary-500)));
  font-weight: 700;
  background-color: rgb(var(--colors-white));
}

.dark .tab-item.is-active {
  background-color: rgb(var(--colors-gray-800));
}

.tab-item.is-active .tab-indicator {
  background-color: rgba(var(--tab-accent, var(--colors-primary-500)));
}

.tabs-content {
  width: 100%;
  background-color: rgb(var(--colors-white));
}

.dark .tabs-content {
  background-color: rgb(var(--colors-gray-800));
}

.tabs-content :deep(.remove-bottom-border) {
  border-bottom: none;
}

@media (min-width: 640px) {
  .tab-item {
    min-width: var(--tab-width-desktop, 20%);
    min-height: 3.5rem;
    padding: 1rem;
    font-size: 0.875rem;
  }
}
</style>
